package app.footyguide.web.data

/**
 * Repositório de competições — Firestore SDK + localStorage cache.
 */
class WebCompetitionRepository {
    data class CompetitionData(
        val id: String,
        val name: String,
        val shortName: String = "",
        val logo: String?,
        val enabled: Boolean = true,
        val priority: Int = Int.MAX_VALUE,
    )

    private var memCache: Map<String, CompetitionData>? = null

    internal fun fallbackCompetitions(): Map<String, CompetitionData> = mapOf(
        "brasileirao" to CompetitionData("brasileirao", "Campeonato Brasileiro Série A", "Brasileirão", "https://storage.googleapis.com/footy-guide.firebasestorage.app/public/logos/competitions/brasileirao.webp?alt=media", true, 1),
        "copa-do-brasil" to CompetitionData("copa-do-brasil", "Copa do Brasil", "Copa do Brasil", "https://storage.googleapis.com/footy-guide.firebasestorage.app/public/logos/competitions/copaDoBrasil.webp?v=2", true, 2),
        "libertadores" to CompetitionData("libertadores", "CONMEBOL Libertadores", "Libertadores", "https://storage.googleapis.com/footy-guide.firebasestorage.app/public/logos/competitions/libertadores.webp?alt=media", true, 3),
    )

    suspend fun getAll(): Map<String, CompetitionData> {
        memCache?.let { return it }

        val cached = LocalCache.get(CACHE_KEY)
        if (cached != null) {
            val result = parseFromJson(cached)
            if (result.isNotEmpty()) {
                memCache = result
                return result
            }
        }

        return try {
            val query = StructuredQuery("competitions")
                .where("enabled", "==", true)

            val docs = FirestoreSdk.runQuery(query)
            if (docs.isEmpty()) {
                val fallback = fallbackCompetitions()
                memCache = fallback
                return fallback
            }
            val result = docs.associate { doc ->
                val name = doc.string("name") ?: doc.id
                val shortName = doc.string("shortName")?.takeIf { it.isNotBlank() }.orEmpty()
                val logo = doc.string("logo")?.takeIf { it.isNotBlank() }
                val priority = doc.int("priority") ?: Int.MAX_VALUE
                doc.id to CompetitionData(
                    id = doc.id,
                    name = name,
                    shortName = shortName,
                    logo = logo,
                    enabled = true,
                    priority = priority,
                )
            }
            memCache = result
            LocalCache.set(CACHE_KEY, serializeToJson(result))
            result
        } catch (_: Exception) {
            val fallback = fallbackCompetitions()
            memCache = fallback
            fallback
        }
    }

    private fun serializeToJson(items: Map<String, CompetitionData>): String {
        val entries = items.entries.joinToString(",") { (id, c) ->
            val logoStr = c.logo?.let { """"${it.replace("\"", "\\\"")}"""" } ?: "null"
            """{"id":"$id","name":"${c.name}","shortName":"${c.shortName}","logo":$logoStr,"enabled":${c.enabled},"priority":${c.priority}}"""
        }
        return "[$entries]"
    }

    private fun parseFromJson(json: String): Map<String, CompetitionData> {
        return try {
            val arr = JSON.parse<dynamic>(json)
            val length = (arr.length as? Number)?.toInt() ?: 0
            (0 until length).mapNotNull { i ->
                val item = arr[i]
                val id = item.id as? String ?: return@mapNotNull null
                val name = item.name as? String ?: id
                val shortName = item.shortName as? String ?: ""
                val logo = item.logo as? String
                val enabled = (item.enabled as? Boolean) ?: true
                val priority = (item.priority as? Number)?.toInt() ?: Int.MAX_VALUE
                id to CompetitionData(
                    id = id,
                    name = name,
                    shortName = shortName,
                    logo = logo,
                    enabled = enabled,
                    priority = priority,
                )
            }.toMap()
        } catch (_: Exception) { emptyMap() }
    }

    companion object {
        private const val CACHE_KEY = "fg-cache-competitions-v5"
    }
}
